import parse from 'html-react-parser';
import { Link } from 'react-router-dom';

import {
    AccessTime,
    CheckCircle,
    School,
    WorkspacePremium,
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useCourses } from '../../context/CourseContext';
import PreviewCertificateModal from './PreviewCertificateModal';
import { useState } from 'react';

const CourseCard = ({ course }) => {
    const theme = useTheme();
    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    // Check if all lessons are completed
    const allLessonsCompleted =
        course.completed_course_items_count === course.course_items_count;

    const { downloadCertificate } = useCourses();

    const handleLaunchPdf = async () => {
        if (!allLessonsCompleted) return;

        try {
            const result = await downloadCertificate(course.id, true);
            if (result.success) {
                setPdfUrl(result.pdfUrl);
                setPdfModalOpen(true);
            }
        } catch (error) {
            console.error('Failed to load certificate:', error);
        }
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${theme.palette.primary.light}`,
                position: 'relative',
            }}
        >
            {allLessonsCompleted && (
                <Link
                    to={`/webinars/lms/${course.slug}`}
                    style={{ width: '100%', textDecoration: 'none' }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            color: theme.palette.primary.main,
                        }}
                    >
                        <Tooltip title="Course completed">
                            <CheckCircle />
                        </Tooltip>
                    </Box>
                </Link>
            )}
            <Link
                to={`/webinars/lms/${course.slug}`}
                style={{ width: '100%', textDecoration: 'none' }}
            >
                <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component={'h2'} gutterBottom>
                        {course.name}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {parse(course.description ?? '')}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        {course?.tags?.map((tag) => (
                            <Chip key={tag} label={tag} size="small" />
                        ))}
                    </Box>
                </CardContent>
            </Link>
            <Box
                sx={{
                    backgroundColor: theme.palette.primary.light,
                    px: 2,
                    position: 'absolute',
                    bottom: '0px !important',
                    width: '100%',
                }}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                >
                    <Link
                        to={`/webinars/lms/${course.slug}`}
                        style={{ width: '100%', textDecoration: 'none' }}
                    >
                        <Box display="flex" alignItems="center" sx={{ py: 1 }}>
                            <AccessTime
                                fontSize="small"
                                sx={{ mr: 0.5, color: 'black' }}
                            />
                            <Typography variant="caption">
                                {(() => {
                                    const minutes =
                                        course.course_items_sum_video_duration;
                                    const hours = (minutes / 60).toFixed(2);
                                    return `${hours} ${
                                        hours > 1 ? 'Hours' : 'Hour'
                                    }`;
                                })()}
                            </Typography>
                        </Box>
                    </Link>
                    <Link
                        to={`/webinars/lms/${course.slug}`}
                        style={{ width: '100%', textDecoration: 'none' }}
                    >
                        <Box display="flex" alignItems="center" sx={{ py: 1 }}>
                            <School
                                fontSize="small"
                                sx={{ mr: 0.5, color: 'black' }}
                            />
                            <Typography variant="caption">
                                {course.completed_course_items_count + 1 ===
                                course.course_items_count + 1
                                    ? course.completed_course_items_count + 1
                                    : course.completed_course_items_count}
                                /{course.course_items_count + 1} Slides
                            </Typography>
                        </Box>
                    </Link>
                    <Box display="flex" alignItems="center" sx={{ py: 1 }}>
                        <Tooltip
                            title={
                                allLessonsCompleted
                                    ? 'Click to get certificate'
                                    : 'Complete all lessons to get certificate'
                            }
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: allLessonsCompleted
                                        ? 'pointer'
                                        : 'not-allowed',
                                    opacity: allLessonsCompleted ? 1 : 0.6,
                                    color: allLessonsCompleted
                                        ? 'black'
                                        : 'inherit',
                                    '&:hover': {
                                        color: allLessonsCompleted
                                            ? 'black'
                                            : 'inherit',
                                    },
                                }}
                                onClick={(e) => {
                                    if (allLessonsCompleted) {
                                        handleLaunchPdf();
                                    }
                                }}
                            >
                                <WorkspacePremium
                                    fontSize="small"
                                    sx={{ mr: 0.5 }}
                                />
                                <Typography variant="caption">
                                    Certificate
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Box>
                </Stack>
            </Box>
            {pdfModalOpen && (
                <PreviewCertificateModal
                    open={pdfModalOpen}
                    setOpen={setPdfModalOpen}
                    pdfUrl={pdfUrl}
                    courseId={course.id}
                />
            )}
        </Card>
    );
};

export default CourseCard;
